<template>
  <graphql-requester
    :gql="$options.GET_SERVICES"
    :variables="variables"
    v-slot="{ loading }"
    @result="onResult"
  >
    <skeleton-provider name="service" v-if="loading" />
    <template v-else-if="!loading && hasData">
      <div
        class="grid grid-cols-1 sm:grid-cols-2 gap-4"
        v-if="!useTiles || !activateServicesForm || !locationId"
      >
        <form-service-provider-dropdown
          :controls="controls"
          :options="options"
          v-model="internalValue"
          @input="onInput"
        />
      </div>
      <form-service-provider-select
        v-else-if="hasOptions"
        :controls="controls"
        :options="options"
        v-model="internalValue"
        @input="onInput"
      />
      <alert-info class="p-2" v-else>
        <input type="hidden" :value="internalValue" />
        No available
        <template v-if="showPayorType">
          {{ controls.payor_type === "nhs" ? "NHS" : controls.payor_type }}
        </template>
        services for {{ controls.patient_type }}
        <template v-if="ignoreAgeType !== true">
          and {{ controls.age_type }}
        </template>
        patients
      </alert-info>
    </template>
    <alert-warning class="p-2" v-else>Something went wrong</alert-warning>
  </graphql-requester>
</template>

<script>
import { GET_SERVICES } from "@/graphql";
import AlertInfo from "@/components/Alert/AlertInfo";
import AlertWarning from "@/components/Alert/AlertWarning";
import GraphqlRequester from "@/components/GraphqlRequester";
import FormServiceProviderSelect from "@/components/Form/FormServiceProviderSelect";
import FormServiceProviderDropdown from "@/components/Form/FormServiceProviderDropdown.vue";
import SkeletonProvider from "@/components/SkeletonProvider";

export default {
  GET_SERVICES,
  components: {
    AlertInfo,
    AlertWarning,
    GraphqlRequester,
    FormServiceProviderSelect,
    FormServiceProviderDropdown,
    SkeletonProvider,
  },
  props: ["value", "controls"],
  data() {
    return {
      internalValue: "",
      data: null,
    };
  },
  computed: {
    activateServicesForm() {
      return !!this.controls?.payor_type && !!this.controls?.patient_type;
    },
    showPayorType() {
      return (
        this.$store.getters.hasNhsPayorType ||
        this.controls?.locations?.settings?.payor_types.includes("nhs")
      );
    },
    services() {
      return this.data?.getScheduleDetails?.services || [];
    },
    availableServices() {
      return (
        this.services.filter(
          (service) => (service.provider_ids || []).length > 0
        ) || []
      );
    },
    hasData() {
      return this.data?.getScheduleDetails?.services;
    },
    hasOptions() {
      //Returns true if services form is disabled because we want to show uninteractive dropdown.
      return this.options.length > 0 || !this.activateServicesForm;
    },
    isSingle() {
      return this.$store.getters.isSingleLocation;
    },
    locationId() {
      return (
        this.$store.getters?.locationId || this.controls?.locations?.location_id
      );
    },
    groupServices() {
      // services grouped by patient_type and payor_type
      let services = this.availableServices.reduce((acc, service) => {
        const key = service.patient_type + "_" + service.payor_type;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(service);
        return acc;
      }, {});

      return services;
    },
    useTiles() {
      return !Object.values(this.groupServices).some(
        (group) => group.length > 6
      );
    },
    ignoreAgeType() {
      return this.$store.getters?.ignoreAgeType;
    },
    options() {
      const patient_type = this.controls.patient_type;
      const age_type = this.controls.age_type;
      const ignoreAgeType = this.$store.getters?.ignoreAgeType;
      const payor_type = this.controls.payor_type;
      return this.availableServices.filter((el) => {
        return (
          el["patient_type"] == patient_type &&
          (ignoreAgeType || el["age_type"] == age_type) &&
          (!this.showPayorType || el["payor_type"] == payor_type)
        );
      });
    },
    variables() {
      if (!this.locationId) return;
      let input = {};
      input.location_id = this.locationId;
      input.promo_code = this.$store.getters.promoCode;
      return { input };
    },
  },
  watch: {
    value: {
      handler(val) {
        this.internalValue = val;
      },
      immediate: true,
    },
    services: {
      handler(value) {
        // this.$emit("http:services", data);
        this.$store.commit("fieldUpdate", {
          key: "services",
          value,
        });
      },
    },
  },
  methods: {
    onInput(val) {
      this.$emit("input:field", val);
    },
    onResult(data) {
      this.data = data;

      const services = data.getScheduleDetails.services;
      const providers = data.getScheduleDetails.providers;
      if (services && providers) {
        services.forEach((service) => {
          const providerIds = service.provider_ids;
          service.providers = providers.filter((p) =>
            providerIds.includes(p.provider_id)
          );
        });
      }
    },
  },
};
</script>
